import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { Button, InputField } from '@components/common'
import { ICampaignTriggerKeyword } from '@dataTypes/marketing.type'
import { Form, Modal } from 'react-bootstrap'
import { VARIABLE_LIST } from '@constants/Marketing'
import './ActionTriggerStyle.css'
import { useAppSelector } from '@store/hooks'
import { replaceVariables } from '@utils/marketing.utils'
import { sanitizeEmailHtml } from '@utils/sanitize-html.util'
import { ModalTextEditor } from '@components/layout/Modals'
import { filterCampaignTemplatesBySendChannel } from '@utils/campaign.util'
import {
  attachEmailCanvasToHtml,
  emailCanvasCssVars,
} from '@utils/marketingTemplate.util'

interface ActionTriggerModalProps {
  viewOnly?: boolean
  show: boolean
  onHide: () => void
  initialData?: ICampaignTriggerKeyword
  handleSave: (data: ICampaignTriggerKeyword) => void
  handleSaveDraft: (data: ICampaignTriggerKeyword) => void
  setPreviewMessage: Dispatch<SetStateAction<string | null>>
  setPreviewSubject?: Dispatch<SetStateAction<string | undefined>>
  /** Matches parent campaign Send via so template list is SMS vs Email only. */
  sendViaChannel?: string
}

const defaultData: ICampaignTriggerKeyword = {
  campaign_id: '',
  name: '',
  keyword: '',
  text_content: '',
  html_content: '',
  status: 'Draft',
}

const ActionTriggerModal: React.FC<ActionTriggerModalProps> = ({
  viewOnly = false,
  show,
  onHide,
  initialData,
  handleSave,
  handleSaveDraft,
  setPreviewMessage,
  setPreviewSubject,
  sendViaChannel = 'SMS',
}) => {
  const campaignTemplates = useAppSelector(
    (state) => state.marketing.campaignTemplates,
  )
  const isEmail = sendViaChannel === 'Email'
  const templatesForSendChannel = useMemo(
    () =>
      filterCampaignTemplatesBySendChannel(campaignTemplates, sendViaChannel),
    [campaignTemplates, sendViaChannel],
  )
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string
  }>({})
  const [data, setData] = useState<ICampaignTriggerKeyword>(
    initialData ?? defaultData,
  )
  const [textInput, setTextInput] = useState<string>(
    initialData?.text_content ?? '',
  )
  const [htmlInput, setHtmlInput] = useState<string>(
    initialData?.html_content ?? '',
  )
  const selectedEmailCanvas = useMemo(() => {
    const id = data.campaign_template_id
    if (!id) return undefined
    return campaignTemplates.find((t) => t.campaign_template_id === id)
      ?.email_editor_canvas
  }, [data.campaign_template_id, campaignTemplates])

  useEffect(() => {
    const id = data.campaign_template_id
    if (!id) return
    const sel = campaignTemplates.find((t) => t.campaign_template_id === id)
    if (sel && sel.channel !== sendViaChannel) {
      setData((prev) => ({ ...prev, campaign_template_id: '' }))
      setTextInput('')
      setHtmlInput('')
    }
  }, [sendViaChannel, data.campaign_template_id, campaignTemplates])

  const generateId = (): string => {
    return initialData && initialData.campaign_trigger_keyword_id
      ? initialData.campaign_trigger_keyword_id
      : `new-${new Date().getTime()}`
  }

  const validateForm = (): boolean => {
    const errors: { [key: string]: string } = {}
    if (!data.name || data.name.trim() === '') {
      errors.name = 'Trigger name is required'
    }
    if (!data.keyword || data.keyword === '') {
      errors.keyword = 'Keyword is required'
    }
    if (isEmail && (!data.subject || data.subject.trim() === '')) {
      errors.subject = 'Input email subject'
    }
    if (!textInput || textInput.trim() === '') {
      errors.text_content = 'Input campaign message'
    }
    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSaveForm = (): void => {
    if (validateForm()) {
      handleSave({
        ...data,
        campaign_trigger_keyword_id: generateId(),
        text_content: textInput,
        html_content: attachEmailCanvasToHtml(htmlInput, selectedEmailCanvas),
        subject: isEmail ? data.subject : undefined,
      })
      onHide()
    }
  }

  const openPreview = (): void => {
    setPreviewMessage(replaceVariables(htmlInput ?? ''))
    setPreviewSubject?.(isEmail ? data.subject : undefined)
  }

  return (
    <>
      <Modal
        show={show}
        onHide={onHide}
        className="action-trigger-modal"
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {viewOnly
              ? 'View action trigger'
              : initialData
                ? 'Edit action trigger'
                : 'Add action trigger'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="custom-modal-body">
          <div className="ac-item">
            <div className="ac-item-input">
              <span className="ac-input-label">Name</span>
              <InputField
                className={
                  validationErrors.name ? 'select-field-destructive' : ''
                }
                destructive={!!validationErrors.name}
                hint={validationErrors.name}
                onChange={(e) => setData({ ...data, name: e.target.value })}
                placeholder="Trigger name"
                value={data.name}
                disabled={viewOnly}
              />
            </div>
            <div className="ac-item-input">
              <span className="ac-input-label">Keyword</span>
              <InputField
                className={
                  validationErrors.keyword ? 'select-field-destructive' : ''
                }
                destructive={!!validationErrors.keyword}
                hint={validationErrors.keyword}
                onChange={(e) =>
                  setData({ ...data, keyword: e.target.value.toUpperCase() })
                }
                placeholder="CODE"
                value={data.keyword}
                disabled={viewOnly}
              />
            </div>
          </div>
          <div className="ac-item">
            <div className="ac-item-input">
              <span className="ac-input-label">Select template (Optional)</span>
              <Form.Select
                name="template"
                value={data.campaign_template_id}
                onChange={(e) => {
                  setData({ ...data, campaign_template_id: e.target.value })
                  const template = campaignTemplates.find(
                    (t) => t.campaign_template_id === e.target.value,
                  )
                  if (template) {
                    setTextInput(template.text_content)
                    setHtmlInput(
                      attachEmailCanvasToHtml(
                        template.html_content,
                        template.email_editor_canvas,
                      ),
                    )
                  }
                }}
                disabled={viewOnly}
              >
                <option value=""></option>
                {templatesForSendChannel.map((template) => (
                  <option
                    key={template.campaign_template_id}
                    value={template.campaign_template_id}
                  >
                    {template.name}
                  </option>
                ))}
              </Form.Select>
            </div>
            <div className="ac-item-input" />
          </div>
          {isEmail && (
            <div className="ac-item">
              <div className="ac-item-input">
                <span className="ac-input-label">Subject</span>
                <InputField
                  className={
                    validationErrors.subject ? 'select-field-destructive' : ''
                  }
                  destructive={!!validationErrors.subject}
                  hint={validationErrors.subject}
                  onChange={(e) =>
                    setData({ ...data, subject: e.target.value })
                  }
                  placeholder="Email subject"
                  value={data.subject ?? ''}
                  disabled={viewOnly}
                />
              </div>
            </div>
          )}
          <div className="ac-item">
            <div className="ac-item-input ci-message-input">
              <span className="ac-input-label">Message</span>
              <div
                className={isEmail ? 'mt-marketing-email-tpl-root' : undefined}
                style={
                  isEmail ? emailCanvasCssVars(selectedEmailCanvas) : undefined
                }
              >
                <ModalTextEditor
                  readOnly={viewOnly}
                  className={
                    validationErrors.text_content
                      ? 'text-editor-destructive'
                      : ''
                  }
                  inputText={textInput}
                  setInputText={setTextInput}
                  inputHtml={htmlInput}
                  setInputHtml={setHtmlInput}
                  variableList={VARIABLE_LIST}
                  showMention={false}
                  maxCharCount={isEmail ? undefined : 200}
                />
                {validationErrors.text_content && (
                  <span className="hint-text">
                    {validationErrors.text_content}
                  </span>
                )}
              </div>
            </div>
            <div className="ac-item-input">
              <span className="ac-input-label">
                Message preview
                <button
                  type="button"
                  className="unstyled-button mgc_fullscreen_2_line"
                  aria-label="Open message preview"
                  onClick={openPreview}
                />
              </span>
              <div
                className={`ac-preview-content ci-message-preview${
                  isEmail ? ' ci-message-preview--email' : ''
                }`}
              >
                <div
                  className={`mpc-text${isEmail ? ' mpc-text--email mt-marketing-email-tpl-root' : ''}`}
                  style={
                    isEmail
                      ? emailCanvasCssVars(selectedEmailCanvas)
                      : undefined
                  }
                  dangerouslySetInnerHTML={{
                    __html: sanitizeEmailHtml(
                      replaceVariables(htmlInput ?? ''),
                    ),
                  }}
                />
              </div>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          {!viewOnly ? (
            <>
              <Button
                size="md"
                className="button-link"
                onClick={() => {
                  handleSaveDraft({
                    ...data,
                    campaign_trigger_keyword_id: generateId(),
                    text_content: textInput,
                    html_content: attachEmailCanvasToHtml(
                      htmlInput,
                      selectedEmailCanvas,
                    ),
                    subject: isEmail ? data.subject : undefined,
                  })
                  onHide()
                }}
                disabled={!data || data.name.trim() === ''}
                data-bs-toggle={
                  !data || data.name.trim() === '' ? 'tooltip' : undefined
                }
                title={
                  !data || data.name.trim() === ''
                    ? 'Trigger name is required'
                    : undefined
                }
              >
                Save as draft
              </Button>

              <div className="ms-auto">
                <>
                  <Button
                    size="md"
                    className="button-secondary me-2"
                    onClick={onHide}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="md"
                    className="button-primary"
                    onClick={handleSaveForm}
                  >
                    Save
                  </Button>
                </>
              </div>
            </>
          ) : (
            <div className="ms-auto">
              <Button
                size="md"
                className="button-secondary me-2"
                onClick={onHide}
              >
                Close
              </Button>
            </div>
          )}
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default ActionTriggerModal
